import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import {
    FaExclamationTriangle,
    FaExchangeAlt,
    FaFileUpload,
    FaRobot,
    FaBalanceScale,
    FaNetworkWired,
    FaBolt,
    FaFolderOpen,
    FaUserShield,
    FaHeartbeat,
    FaCreditCard,
    FaLandmark,
    FaShieldAlt,
    FaClipboardCheck,
    FaCertificate,
    FaUserSecret,
} from "react-icons/fa";
import { categoryLabels } from "../../utils/categoryLabels";
import { getReadinessColor } from "../../utils/readinessUtils";

// Category icons mapping
const categoryIcons = {
    "behavioural_threats": <FaExclamationTriangle className="text-yellow-500 text-xl" />,
    "input_output_risks": <FaExchangeAlt className="text-blue-500 text-xl" />,
    "malicious_doc_uploads": <FaFileUpload className="text-pink-500 text-xl" />,
    "llm_api_mis_use_abuse": <FaRobot className="text-green-500 text-xl" />,
    "policy_compliance_tests": <FaBalanceScale className="text-purple-500 text-xl" />,
    "llm_dos": <FaBolt className="text-amber-500 text-xl" />,
    "mcp_agent": <FaNetworkWired className="text-orange-500 text-xl" />,
    "rc_gdpr": <FaUserShield className="text-blue-500 text-xl" />,
    "rc_hipaa": <FaHeartbeat className="text-red-500 text-xl" />,
    "rc_pci_dss": <FaCreditCard className="text-green-500 text-xl" />,
    "rc_eu_ai_act": <FaLandmark className="text-indigo-500 text-xl" />,
    "rc_owasp_llm": <FaShieldAlt className="text-orange-500 text-xl" />,
    "rc_nist_ai_rmf": <FaClipboardCheck className="text-teal-500 text-xl" />,
    "rc_soc2": <FaCertificate className="text-purple-500 text-xl" />,
    "prompt_injection": <FaUserSecret className="text-rose-500 text-xl" />,
};

// Helper to get category key from display label
const getCategoryKey = (displayLabel) => {
    return Object.keys(categoryLabels).find(key => categoryLabels[key] === displayLabel) || null;
};

// Get border color based on theme
const getBorderColor = () => {
  const theme = document.body.getAttribute('data-theme');
  return theme === 'dark' ? '#8b5cf6' : '#ec4899';
};

// Tooltip component with React state
const Tooltip = ({ text, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 15,
        left: rect.left + rect.width / 2
      });
    }
  };

  const handleMouseEnter = () => {
    updatePosition();
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isVisible) {
        updatePosition();
      }
    };

    const handleResize = () => {
      if (isVisible) {
        updatePosition();
      }
    };

    if (isVisible) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isVisible]);

  const theme = document.body.getAttribute('data-theme');
  const bgColor = theme === 'dark' ? '#1e293b' : '#333';
  const textColor = theme === 'dark' ? '#e2e8f0' : 'white';

  return (
    <>
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'help' }}
      >
        {children}
      </div>
      {isVisible && (
        <div
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translate(-50%, -100%)',
            marginTop: '-8px',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            whiteSpace: 'normal',
            zIndex: 100000,
            backgroundColor: bgColor,
            color: textColor,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            maxWidth: '280px',
            minWidth: '200px',
            wordWrap: 'break-word',
            lineHeight: '1.4',
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease',
          }}
        >
          {text}
          <div
            style={{
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              borderWidth: '5px',
              borderStyle: 'solid',
              borderColor: `${bgColor} transparent transparent transparent`,
            }}
          />
        </div>
      )}
    </>
  );
};

const customStyles = {
  table: {
    style: {
      width: "100%",
      borderCollapse: "collapse",
      backgroundColor: "var(--card-bg)",
      color: "var(--card-text)",
      border: "none",
    },
  },
  tableWrapper: {
    style: {
      backgroundColor: "var(--card-bg)",
      overflowX: "auto",
      overflowY: "visible",
      width: "100%",
      border: "none",
    },
  },
  headRow: {
    style: {
      backgroundColor: "var(--card-bg)",
      color: "var(--card-text)",
      border: "none",
      overflow: "visible",
      position: "relative",
    },
  },
  headCells: {
    style: {
      textAlign: "left",
      padding: "10px",
      fontWeight: "bold",
      color: "var(--card-text)",
      backgroundColor: "var(--card-bg)",
      border: "none",
      overflow: "visible",
      position: "relative",
    },
  },
  rows: {
    style: {
      border: "none",
      backgroundColor: "var(--card-bg)",
      "&:hover": {
        backgroundColor: "var(--card-bg)",
      },
    },
  },
  cells: {
    style: {
      padding: "12px",
      color: "var(--card-text)",
      backgroundColor: "var(--card-bg)",
      wordWrap: "break-word",
      border: "none",
    },
  },
};

// function to preprocess data (show all rows, even with 0 tests)
const preprocessData = (data) => {
  let lastCategory = null;
  return data.map((row) => {
    const displayCategory = row.category !== lastCategory ? row.category : "";
    lastCategory = row.category;
    return { ...row, displayCategory };
  });
};

const DetailReportTable = ({ data }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_common" });
  const processedData = preprocessData(data);
  const borderColor = getBorderColor();

  const columns = [
    {
      name: t("category"),
      width: "12%",
      cell: (row) =>
        row.displayCategory ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {categoryIcons[getCategoryKey(row.displayCategory)] || <FaFolderOpen className="text-gray-500 text-xl" />}
            <span
              style={{
                fontWeight: "500",
                fontSize: "0.85rem",
                color: "var(--card-text)",
              }}
            >
              {row.displayCategory}
            </span>
          </div>
        ) : (
          <span></span>
        ),
    },
    {
      name: t("subCategory"),
      selector: (row) => row.subcategory,
      width: "18%",
    },
    {
      name: t("securityReadiness"),
      selector: (row) => row.readiness,
      width: "18%",
      cell: (row) => (
        <div style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ 
            fontSize: "0.75rem", 
            fontWeight: "600",
            color: "var(--card-text)",
            minWidth: "30px"
          }}>
            {row.readiness}%
          </span>
          <div style={{ 
            flex: 1, 
            height: "8px", 
            backgroundColor: "#e5e7eb", 
            borderRadius: "4px",
            overflow: "hidden"
          }}>
            {/* 68-100: high (green), 34-67: medium (yellow), 16-33: low (orange), 0-15: critical (dark red) */}
            <div style={{
              height: "100%",
              width: `${row.readiness}%`,
              backgroundColor: getReadinessColor(row.readiness),
              transition: "width 0.3s ease"
            }} />
          </div>
        </div>
      ),
    },
    {
      name: t("totalTests"),
      selector: (row) => row.totalScans,
      width: "13%",
    },
    {
      name: (
          <Tooltip text={t("blockedTooltip")}>
          <span>{t("blocked")}</span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '14px',
            height: '14px',
            backgroundColor: '#6c757d',
            color: 'white',
            borderRadius: '50%',
            fontSize: '11px',
            fontWeight: 'bold',
            cursor: 'help',
            lineHeight: '1',
            textAlign: 'center',
            fontStyle: 'italic',
            transition: 'background-color 0.2s ease'
          }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#495057'} 
             onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}>i</span>
        </Tooltip>
      ),
      selector: (row) => row.scanPassed,
      width: "13%",
    },
    {
      name: (
        <Tooltip text={t("failedToBlockTooltip")}>
          <span>{t("failedToBlock")}</span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '14px',
            height: '14px',
            backgroundColor: '#6c757d',
            color: 'white',
            borderRadius: '50%',
            fontSize: '11px',
            fontWeight: 'bold',
            cursor: 'help',
            lineHeight: '1',
            textAlign: 'center',
            fontStyle: 'italic',
            transition: 'background-color 0.2s ease'
          }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#495057'} 
             onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}>i</span>
        </Tooltip>
      ),
      selector: (row) => row.scanFailed,
      width: "12%",
    },
    {
      name: t("avgResponseTime"),
      selector: (row) => row.averageResponseTime,
      width: "14%",
      cell: (row) => (
        <span>
          {typeof row.averageResponseTime === "number" && row.averageResponseTime > 0
            ? `${row.averageResponseTime.toFixed(3)}${t("secondsUnit")}`
            : t("na")}
        </span>
      ),
    },
  ];

  return (
    <div style={{ 
      overflowX: "hidden", 
      overflowY: "visible",
      width: "100%", 
      backgroundColor: "var(--card-bg)",
      border: "none",
      borderRadius: "8px",
      position: "relative"
    }}>
      <style>{`
        .rdt_TableWrapper {
          border: none !important;
          overflow: visible !important;
          overflow-x: auto !important;
          overflow-y: visible !important;
        }
        .rdt_Table {
          border-collapse: collapse !important;
          overflow: visible !important;
        }
        .rdt_TableHead .rdt_TableRow .rdt_TableCell {
          border: none !important;
          overflow: visible !important;
          position: relative !important;
        }
        .rdt_TableHead {
          overflow: visible !important;
          overflow-x: auto !important;
          overflow-y: visible !important;
          position: relative !important;
          z-index: 1 !important;
        }
        .rdt_TableHeadRow {
          overflow: visible !important;
        }
        .rdt_TableBody .rdt_TableRow .rdt_TableCell {
          border: none !important;
        }
        .rdt_TableHead .rdt_TableRow {
          border: none !important;
          overflow: visible !important;
          position: relative !important;
        }
        .rdt_TableBody .rdt_TableRow {
          border: none !important;
        }
        .rdt_TableCol {
          overflow: visible !important;
        }
        .rdt_TableHeadCell {
          overflow: visible !important;
        }
      `}</style>
      <DataTable
        title=""
        columns={columns}
        customStyles={customStyles}
        data={processedData}
        striped={false}
        highlightOnHover={false}
        dense
        defaultSortFieldId={null}
        noDataComponent={<div style={{ padding: "20px", textAlign: "center", color: "var(--card-text)" }}>{t("noDataAvailable")}</div>}
      />
    </div>
  );
};

export default DetailReportTable;
